import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Edit, Trash2, Play } from 'lucide-react';
import api from '../../services/api';
import ConfirmationModal from '../../components/ConfirmationModal';

export default function AdminExamDetail() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exam, setExam] = useState(null);
  const [components, setComponents] = useState([]);
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState({ highest: null, lowest: null });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadExam(); }, [examId]);

  const loadExam = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/exams/${examId}/marks`);
      if (res.data.success && res.data.data) {
        const comps = res.data.data.components || [];
        const studs = res.data.data.students || [];
        setComponents(comps);
        setStudents(studs);
        const totals = studs.map(s => parseFloat(s.total_marks) || 0);
        const highest = totals.length ? Math.max(...totals) : null;
        const nonZeroTotals = totals.filter(t => t > 0);
        const lowest = nonZeroTotals.length ? Math.min(...nonZeroTotals) : null;
        const average = totals.length ? (totals.reduce((a,b) => a + b, 0) / totals.length) : null;
        setStats({ highest, lowest, average });
        // fetch exam summary from /admin/exams list
        const all = await api.get('/admin/exams');
        const found = (all.data.exams || []).find(e => String(e.exam_id) === String(examId));
        if (found) setExam(found);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load exam');
    } finally { setLoading(false); }
  };

  const deleteExam = async () => {
    if (!confirm('Delete this exam? This will remove marks and data.')) return;
    try {
      setDeleting(true);
      await api.delete(`/lecturer/exams/${examId}`); // re-use lecturer endpoint for delete
      navigate('/admin/exams');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete exam');
    } finally {
      setDeleting(false);
    }
  };

  const releaseMarks = async () => {
    try {
      setReleasing(true);
      setError('');
      setSuccess('');

      const absentStudents = students.filter(s => {
        const cm = s.component_marks || [];
        if (cm.length === 0) return true; // no marks at all
        const anyEntered = cm.some(m => m.marks_obtained !== '' && m.marks_obtained !== null && m.marks_obtained !== undefined);
        return !anyEntered;
      });

      if (absentStudents.length === 0) {
        setSuccess('No absent students to release marks for');
        return;
      }

      let processed = 0;
      for (const stu of absentStudents) {
        const cm = stu.component_marks ? [...stu.component_marks] : [];
        for (const c of components) {
          const existing = cm.find(x => Number(x.component_id) === Number(c.component_id));
          if (existing) {
            existing.marks_obtained = 0;
          } else {
            cm.push({ component_id: c.component_id, marks_obtained: 0 });
          }
        }

        setStudents(prev => prev.map(p => p.student_id === stu.student_id ? { ...p, component_marks: cm, total_marks: cm.reduce((s, m) => s + (Number(m.marks_obtained) || 0), 0) } : p));

        try {
          await saveStudentMarks(stu.student_id, cm);
          processed++;
        } catch (err) {
        }
      }

      setSuccess(`Released marks for ${processed} student(s)`);
      await loadExam();
    } catch (err) {
      setError('Failed to release marks');
    } finally {
      setReleasing(false);
      setShowReleaseModal(false);
    }
  };

  const saveStudentMarks = async (studentId, componentMarks) => {
    try {
      const componentsToSend = (componentMarks || []).map(cm => ({ componentId: cm.component_id, marksObtained: cm.marks_obtained === '' ? null : Number(cm.marks_obtained) })).filter(c => c.marksObtained !== null && !isNaN(c.marksObtained));
      if (componentsToSend.length === 0) { setError('No marks entered'); setTimeout(() => setError(''),2000); return; }
      const totalMarks = componentsToSend.reduce((sum, c) => sum + c.marksObtained, 0);
      await api.post(`/lecturer/exams/${examId}/marks`, { marks: [{ studentId, totalMarks, components: componentsToSend }] });
      loadExam();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save marks');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate('/admin/exams')} className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium mb-2">
            <span className="inline-flex items-center justify-center w-8 h-8 bg-indigo-50 text-indigo-600 rounded-full"><svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/></svg></span>
            Back to exams
          </button>

          <h1 className="text-2xl font-bold">{exam?.exam_name || 'Exam'}</h1>
          <p className="text-gray-500 mt-1">{exam?.grade_name} — {exam?.exam_date ? new Date(exam.exam_date).toLocaleDateString() : '-'}</p>
        </div>

        
      </div>

      {error && <div className="bg-red-50 text-red-700 p-3 rounded">{error}</div>}
      {success && <div className="bg-green-50 text-green-700 p-3 rounded">{success}</div>}

      {/* Release Marks Confirmation Modal */}
      <ConfirmationModal
        isOpen={showReleaseModal}
        onClose={() => setShowReleaseModal(false)}
        onConfirm={() => releaseMarks()}
        title="Release marks for absent students"
        message={`This will set all component marks to 0 for students who are currently marked AB (no marks entered) in this exam.\n\nThis action cannot be undone. Do you want to continue?`}
        confirmText={releasing ? 'Processing...' : 'Confirm'}
        cancelText="Cancel"
        type="warning"
      />

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-6 flex flex-col">
          <div className="text-sm text-gray-500">Highest</div>
          <div className="text-2xl font-bold text-indigo-700 mt-2">{stats.highest !== null ? stats.highest : '-'}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 flex flex-col">
          <div className="text-sm text-gray-500">Average</div>
          <div className="text-2xl font-bold text-green-600 mt-2">{stats.average !== null ? stats.average.toFixed(2) : '-'}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 flex flex-col">
          <div className="text-sm text-gray-500">Lowest</div>
          <div className="text-2xl font-bold text-gray-800 mt-2">{stats.lowest !== null ? stats.lowest : '-'}</div>
        </div>
      </div>

      {/* Components / Breakdown */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Mark Breakdown</h3>
          <div className="text-sm px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full">Total: {components.reduce((s,c) => s + (parseInt(c.max_marks) || 0), 0)} marks</div>
        </div>
        <div className="space-y-2">
          {components.map(c => (
            <div key={c.component_id} className="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
              <div className="text-sm text-gray-700">{c.component_name}</div>
              <div className="text-sm font-semibold text-indigo-600">{c.max_marks} marks</div>
            </div>
          ))}
          {components.length === 0 && <div className="text-sm text-gray-400">No components defined</div>}
        </div>
      </div>

      {/* Students list */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Student Marks</h3>
          <div className="text-sm text-gray-500">{students.length} students</div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="pb-2">Student</th>
                <th className="pb-2">Marks Breakdown</th>
                <th className="pb-2">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {students.map((stu) => {
                const studentMarks = stu.component_marks || [];
                const total = studentMarks.reduce((sum, m) => sum + (parseFloat(m.marks_obtained) || 0), 0);
                const isAbsent = total === 0;
                return (
                  <tr key={stu.student_id} className={`${isAbsent ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                    <td className="py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-semibold">{stu.full_name?.charAt(0)}</div>
                        <div>
                          <div className="font-medium text-gray-800">{stu.full_name}</div>
                          <div className="text-xs text-gray-500">{stu.student_id_number || ''}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2">
                      <div className="flex gap-2 flex-wrap">
                        {components.map(c => {
                          const markObj = (studentMarks || []).find(m => m.component_id === c.component_id);
                          const val = markObj ? (markObj.marks_obtained === '' ? '-' : markObj.marks_obtained) : '-';
                          return <span key={c.component_id} className="px-2 py-1 bg-gray-100 rounded-full text-xs">{c.component_name}: {val}</span>
                        })}
                      </div>
                    </td>

                    <td className="py-2 font-semibold text-gray-800">{isAbsent ? <span className="text-red-600">AB</span> : total}</td>
                  </tr>
                );
              })}
              {students.length === 0 && (
                <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400">No students enrolled in this exam</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}